import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { userAPI } from '../UserAPI';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { ProductPageLink } from "../components/ProductPageLink";
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronRight, FileText } from 'lucide-react';
import type { Column, ColumnDef } from '@tanstack/react-table';

import { Asset, EntityQuery, LinkedEntityQuery, Product, Query, RelationshipDirection, Document, is, and } from '@the-real-insight/tri-model';
import { configuration } from '../Configuration';

import MaterialSymbol from "../components/MaterialSymbol";
import { resolveDin276Style } from "../utilities/din276Style";

import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";

class Properties {
    building: any;
}

/** Klickbarer Spaltenkopf — ersetzt die `sortable`-Köpfe der abgelösten Tabelle. */
function SortableHeader({
    column,
    children,
}: {
    column: Column<any, unknown>;
    children: ReactNode;
}) {
    const sorted = column.getIsSorted();

    return (
        <Button
            type="button"
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
            onClick={() => column.toggleSorting(sorted === "asc")}
        >
            {children}
            {sorted === "asc" ? (
                <ArrowUp aria-hidden className="h-3.5 w-3.5" />
            ) : sorted === "desc" ? (
                <ArrowDown aria-hidden className="h-3.5 w-3.5" />
            ) : (
                <ArrowUpDown aria-hidden className="h-3.5 w-3.5 opacity-50" />
            )}
        </Button>
    );
}

export default function TechnicalEquipmentProperties(properties: Properties) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [assets, setAssets] = useState([] as Asset[]);
    const [products, setProducts] = useState([] as Product[]);

    const assetColumns = useMemo<ColumnDef<any, any>[]>(() => [{
        id: 'open',
        header: '',
        size: 40,
        enableSorting: false,
        cell: ({ row }) => (
            <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t('entityPage.openDevice')}
            title={t('entityPage.openDevice')}
                onClick={() => {
                    navigate(`/entity?selectedEntityType=Asset&selectedEntity=${row.original._id}`);
                }}
            >
                <ChevronRight aria-hidden="true" className="h-4 w-4" />
            </Button>
        ),
    }, {
        id: 'type',
        header: ({ column }) => (
            <SortableHeader column={column}>
                {t('buildingProperties.technicalEquipmentPropertiesPanel.type')}
            </SortableHeader>
        ),
        size: 320,
        accessorFn: (asset: any) => asset.schemaCategoryName,
        cell: ({ row }) => (
            <div className='flex min-w-0 items-center gap-2'>
                {row.original.categoryIcon ? (
                    <MaterialSymbol name={row.original.categoryIcon} color={row.original.categoryColor} size={20} />
                ) : (
                    /* Farbpunkt der Kategorie — die Farbe ist ein Datenwert und
                       bleibt deshalb wie im Bestand ein Inline-Wert. */
                    <span
                        className={row.original.categoryColor ? 'inline-block h-3 w-3 shrink-0 rounded-sm' : 'inline-block h-3 w-3 shrink-0 rounded-sm border'}
                        style={row.original.categoryColor ? { backgroundColor: row.original.categoryColor } : undefined}
                    />
                )}
                <div className='min-w-0 break-words text-sm'>{row.original.schemaCategoryName}</div>
            </div>
        ),
    }, {
        id: 'product',
        header: ({ column }) => (
            <SortableHeader column={column}>
                {t('buildingProperties.technicalEquipmentPropertiesPanel.product')}
            </SortableHeader>
        ),
        size: 150,
        accessorFn: (asset: any) => asset.productName,
        cell: ({ row }) => (
            <div className='min-w-0 break-words text-sm'>
                <ProductPageLink product={row.original.product}>{row.original.productName}</ProductPageLink>
            </div>
        ),
    }, {
        id: 'installed',
        header: ({ column }) => (
            <SortableHeader column={column}>
                {t('buildingProperties.technicalEquipmentPropertiesPanel.installed')}
            </SortableHeader>
        ),
        size: 130,
        accessorFn: (asset: any) => asset.installed ? new Date(asset.installed).getTime() : 0,
        cell: ({ row }) => (
            <div className='text-sm'>{(() => {
                if (!row.original.installed) return '-';
                const d = new Date(row.original.installed);
                return isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
            })()}</div>
        ),
    }, {
        id: 'lastMaintained',
        header: ({ column }) => (
            <SortableHeader column={column}>
                {t('buildingProperties.technicalEquipmentPropertiesPanel.lastMaintained')}
            </SortableHeader>
        ),
        size: 130,
        accessorFn: (asset: any) => asset.lastMaintained ? new Date(asset.lastMaintained).getTime() : 0,
        cell: ({ row }) => (
            <div className='text-sm'>{(() => {
                if (!row.original.lastMaintained) return '-';
                const d = new Date(row.original.lastMaintained);
                return isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
            })()}</div>
        ),
    }], [navigate, t]);

    const productColumns = useMemo<ColumnDef<any, any>[]>(() => [{
        id: 'document',
        header: t('buildingProperties.technicalEquipmentPropertiesPanel.document'),
        accessorFn: (product: any) => product._id,
        enableSorting: false,
        cell: ({ row }) => (
            row.original.manual?.url ? (
                <a href={`${configuration.serverUrl}${row.original.manual.url}`} target="_blank" rel="noreferrer" className="inline-flex items-center">
                    <FileText aria-hidden="true" className="h-4 w-4" />
                </a>
            ) : (
                <></>
            )
        ),
    }, {
        id: 'product',
        header: ({ column }) => (
            <SortableHeader column={column}>
                {t('buildingProperties.technicalEquipmentPropertiesPanel.product')}
            </SortableHeader>
        ),
        accessorFn: (product: any) => product.name,
        cell: ({ row }) => (
            <div className='min-w-0 break-words text-sm'>{row.original.name}</div>
        ),
    }], [t]);

    useEffect(() => {
        const call = async () => {
            try {
                console.log('Retrieving metadata');

                const metadata = (await userAPI.getDIN276Metadata()).reduce((map: any, category: any) => {
                    map[category.code] = category;

                    return map;
                }, {});

                console.log('Retrieving assets');

                // Scope assets to this building. Assets carry the building's flat
                // address (street/streetNumber/postalCode) — same linkage AssetList
                // uses for scopeBuildings. tri-model Building instances may not expose
                // those scalars as own properties, so read them via a JSON round-trip.
                const building = properties.building || {};
                let flat: any = building;
                try {
                    flat = JSON.parse(JSON.stringify(building));
                } catch {
                    flat = building;
                }

                const assetFilter = and(
                    is('street').equal(flat.street),
                    is('streetNumber').equal(flat.streetNumber),
                    is('postalCode').equal(flat.postalCode)
                );

                const result = (await userAPI.query(
                    new Query(new EntityQuery(Asset.type, [], undefined, assetFilter))
                )).map((asset: any) => {
                    const category = asset.schemaCategory ? metadata[asset.schemaCategory] : undefined;
                    const style = resolveDin276Style(asset.schemaCategory);

                    return {
                        ...asset,
                        schemaCategoryName: category && category.name ? category.name : '-',
                        categoryColor: (category && category.color) || style.color,
                        categoryIcon: (category && category.icon) || style.icon,
                    };
                });

                console.log(result);

                setAssets(result as Asset[]);

            } catch (error) {
            }
        };

        call();
    }, [properties]);

    useEffect(() => {
        const call = async () => {
            try {
                const result = await userAPI.query(new Query(new EntityQuery(Product.type),
                    [new LinkedEntityQuery('manual', RelationshipDirection.out, Document.type)]));

                console.log(result);

                setProducts(result as Product[]);
            } catch (error) {
            }
        };

        call();
    }, [properties]);

    // Documentation table: only products referenced by this building's assets
    // that actually carry a manual/document (not the whole product catalogue).
    const assetProductIds = new Set(
        (assets as any[]).map((asset: any) => asset.product).filter(Boolean)
    );
    const buildingProducts = (products as any[]).filter(
        (product: any) => assetProductIds.has(product._id) && product.manual?.url
    );

    return <div className="flex min-w-0 max-w-[1400px] flex-col gap-4">
        <h3 className="text-lg font-bold leading-tight text-[color:var(--customer-primary-dark,hsl(var(--foreground)))]">
            {t('buildingProperties.technicalEquipmentPropertiesPanel.assets')}
        </h3>
        <div className="overflow-x-auto">
            <DataTable
                columns={assetColumns}
                data={assets as any[]}
                pageSize={10}
                emptyMessage={t('buildingProperties.technicalEquipmentPropertiesPanel.noAssets')}
            />
        </div>
        {buildingProducts.length > 0
            ?
            <>
                <h3 className="text-lg font-bold leading-tight text-[color:var(--customer-primary-dark,hsl(var(--foreground)))]">
                    {t('buildingProperties.technicalEquipmentPropertiesPanel.documentation')}
                </h3>
                <div className="overflow-x-auto">
                    <DataTable
                        columns={productColumns}
                        data={buildingProducts}
                        pageSize={10}
                        emptyMessage={t('settingsPanel.dataTable.noRecordsText')}
                    />
                </div>
            </>
            :
            <></>
        }
    </div>;
}
